import React, { useState, useEffect, useMemo } from "react";
import cx from "classnames";
import MappingFieldCell from "./MappingFieldCell";
import MappingGroupNotice from "./MappingGroupNotice";
import DeleteMappingModal from "./DeleteMappingModal";
import { EditOutlined, Close } from "@mui/icons-material";
import DeleteOutline from "~/components/Icons/DeleteOutline";

const MappingGroup = ({
  mapping,
  index,
  fields,
  isValidColumnMapping,
  editing,
  editingLocked,
  isInUse,
  isOnlyMapping,
  isNew,
  onStartEdit,
  onStopEdit,
  onSave,
  onCreate,
  onDelete,
  dropFnRef,
}) => {
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInUseTooltip, setShowInUseTooltip] = useState(null);
  const [draft, setDraft] = useState({ name: mapping.name, mappings: mapping.mappings });
  const [showRequiredWarning, setShowRequiredWarning] = useState(false);

  // reset the draft on fieldMappingGroups update or we leave editing
  useEffect(() => {
    if (!editing) {
      setDraft({ name: mapping.name, mappings: mapping.mappings });
      setShowRequiredWarning(false);
    }
  }, [mapping, editing]);

  // while editing, own the shared drop handler so DataSubmission's onDragEnd routes drops here
  useEffect(() => {
    if (!editing || !dropFnRef) return undefined;
    dropFnRef.current = (fieldName, columnName) => {
      setDraft((prev) => ({
        ...prev,
        mappings: { ...prev.mappings, [fieldName]: columnName },
      }));
    };
    return () => {
      dropFnRef.current = null;
    };
  }, [editing, dropFnRef]);

  const removeField = (fieldName) => {
    setDraft((prev) => ({
      ...prev,
      mappings: { ...prev.mappings, [fieldName]: null },
    }));
  };

  const isFieldEdited = (fieldName) =>
    (mapping.mappings[fieldName] ?? null) !== (draft.mappings[fieldName] ?? null);

  const nameEdited = draft.name !== mapping.name;

  const missingRequiredFields = useMemo(
    () => fields.filter((field) => field.required && !draft.mappings[field.name]),
    [fields, draft.mappings]
  );

  // the warning clears itself as soon as the last missing field is mapped
  const requiredWarning = showRequiredWarning && missingRequiredFields.length > 0;
  const warnedFieldNames = requiredWarning ? missingRequiredFields.map((field) => field.name) : [];
  const requiredWarningFields = missingRequiredFields.map((field) => field.title).join(", ");

  // send only the fields (and name) that actually changed
  const diffChanges = () => {
    const changes = {};
    if (nameEdited) changes.name = draft.name;
    fields.forEach((field) => {
      if (isFieldEdited(field.name)) changes[field.name] = draft.mappings[field.name] ?? null;
    });
    return changes;
  };

  const save = () => {
    if (missingRequiredFields.length > 0) {
      setShowRequiredWarning(true);
      return;
    }

    setShowRequiredWarning(false);

    if (isNew) {
      const assignments = {};
      fields.forEach((field) => {
        if (draft.mappings[field.name]) assignments[field.name] = draft.mappings[field.name];
      });

      setUpdating(true);
      onCreate({ name: draft.name.trim() || `Mapping-${index + 1}`, mappings: assignments })
        .then(() => onStopEdit())
        .catch(() => {})
        .finally(() => setUpdating(false));
      return;
    }

    const changes = diffChanges();
    if (Object.keys(changes).length === 0) {
      onStopEdit();
      return;
    }

    setUpdating(true);
    onSave(mapping.id, changes)
      .then(() => onStopEdit())
      .catch(() => {
        // leave editing open so the user can retry; the hook surfaces the error notice
      })
      .finally(() => setUpdating(false));
  };

  const confirmDelete = () => {
    setDeleting(true);
    onDelete(mapping.id)
      .then(() => setShowDeleteModal(false))
      .catch(() => {
        // keep the modal open on failure
      })
      .finally(() => setDeleting(false));
  };

  const handleShowInUseTooltip = () => {
    if (isOnlyMapping) {
      setShowInUseTooltip("Minimum one mapping required");
    } else {
      setShowInUseTooltip("Mapping is in use");
    }

    setTimeout(() => {
      setShowInUseTooltip(null);
    }, 2000);
  };

  const renderEditButton = () => (
    <div
      className="button button--white button--sq-md"
      aria-label={editing ? "Cancel editing" : "Edit mapping"}
      role="button"
      onClick={editing ? onStopEdit : editingLocked ? undefined : onStartEdit}
    >
      {editing ? (
        <Close style={{ fontSize: "16px" }} />
      ) : (
        <EditOutlined style={{ fontSize: "16px" }} />
      )}
    </div>
  );

  const renderDeleteButton = () =>
    editing ? (
      <div
        className="mapping__group__header__save"
        role="button"
        onClick={updating ? undefined : save}
      >
        {updating ? <div className="loading-indicator loading-indicator--no-margin" /> : "Save"}
      </div>
    ) : (
      <div style={{ position: "relative" }}>
        <div
          className="button button--white button--sq-md"
          aria-label="Delete mapping"
          role="button"
          onClick={() =>
            isInUse || isOnlyMapping ? handleShowInUseTooltip() : setShowDeleteModal(true)
          }
        >
          <DeleteOutline size="small" />
        </div>
        {showInUseTooltip && (
          <div className="tooltip mapping__group__del-tooltip">
            <div className="tooltip__container">
              <div className="tooltip__container__subtitle">{showInUseTooltip}</div>
            </div>
          </div>
        )}
      </div>
    );

  return (
    <div
      className={cx("mapping__group", {
        "mapping__group--saving": updating,
        "mapping__group--editing": editing,
      })}
    >
      {updating && <div className="mapping__group__overlay"></div>}
      <div className="mapping__group__header">
        {editing ? (
          <input
            className={cx("mapping__group__header__input", {
              "mapping__group__header__input--edited": nameEdited,
            })}
            value={draft.name ?? ""}
            placeholder={`Mapping-${index + 1}`}
            onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
          />
        ) : (
          <div className="mapping__group__header__name">
            {mapping.name || `Mapping-${index + 1}`}
          </div>
        )}
        <div className="mapping__group__header__actions">
          {renderEditButton()}
          {renderDeleteButton()}
        </div>
      </div>
      {requiredWarning && <MappingGroupNotice requiredFields={requiredWarningFields} />}
      {fields.map((field) => {
        const value = draft.mappings[field.name];

        return (
          <MappingFieldCell
            key={field.name}
            mappingId={mapping.id}
            mappingName={draft.name}
            field={field}
            value={value}
            isValid={isNew || isValidColumnMapping(field.name, value)}
            editing={editing}
            edited={editing && isFieldEdited(field.name)}
            requiredFieldErrors={warnedFieldNames.includes(field.name)}
            onRemove={removeField}
          />
        );
      })}
      {showDeleteModal && (
        <DeleteMappingModal
          mappingName={mapping.name || `Mapping-${index + 1}`}
          deleting={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default MappingGroup;
